import { Shape } from '../../../../types';

/**
 * System-clipboard serialization for map-builder copy/paste.
 *
 * The in-memory `clipboard` state in MapEditorProvider only lives within one
 * React tree, so pasting in a different tab/window doesn't see what the
 * source tab copied. To bridge tabs we mirror the same shapes payload to the
 * OS clipboard via `navigator.clipboard.writeText`, prefixed with a sentinel
 * line so we can recognize our own format on read.
 *
 * The sentinel + JSON shape is deliberately human-readable: pasting it into
 * a chat or editor surfaces obvious "this is a map clipboard payload" text
 * rather than mojibake. If clipboard access is denied (Firefox without
 * permission, sandboxed iframes, http://) the in-memory clipboard still
 * works as a same-tab fallback.
 */

const SENTINEL = '__tactic_map_clipboard__';
const VERSION = 1;

interface ClipboardPayload {
  version: number;
  shapes: Shape[];
}

export async function writeShapesToSystemClipboard(
  shapes: Shape[]
): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    return;
  }
  const payload: ClipboardPayload = { version: VERSION, shapes };
  const text = `${SENTINEL}\n${JSON.stringify(payload)}`;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Clipboard write rejected (perm, focus, sandboxing). The in-memory
    // clipboard in MapEditorProvider still gets set by the caller, so
    // same-tab paste continues to work.
  }
}

export async function readShapesFromSystemClipboard(): Promise<Shape[] | null> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
    return null;
  }
  let text: string;
  try {
    text = await navigator.clipboard.readText();
  } catch {
    return null;
  }
  if (!text.startsWith(`${SENTINEL}\n`)) return null;
  const jsonPart = text.slice(SENTINEL.length + 1);
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonPart);
  } catch {
    return null;
  }
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    (parsed as ClipboardPayload).version !== VERSION ||
    !Array.isArray((parsed as ClipboardPayload).shapes)
  ) {
    return null;
  }
  return (parsed as ClipboardPayload).shapes;
}
